<template>
  <v-row no-gutters class="pt-5">
    <v-col class="pr-2">
      <card-title-row translation-path="sidebar.fireSimulation.title" :has-buttons="false" />
      <sidebar-row class="pt-2">
        <v-row no-gutters style="background-color: white">
          <v-col cols="auto" class="text-left">
            <v-icon color="primary" class="mr-2" size="24">mdi-information</v-icon>
          </v-col>
          <v-col cols="10" class="text-left fire-simulation-info" :style="`color: ${this.$_colors.primary}`">
            {{ $i18n.t('sidebar.fireSimulation.titleInfo') }}
          </v-col>
        </v-row>
      </sidebar-row>
      <v-form ref="fireSimulationForm" v-model="isValid">
        <sidebar-row class="pt-5">
          <v-row no-gutters class="justify-space-between">
            <v-col cols="4" class="text-body-2 center-content text-left pt-8">
              {{ $i18n.t('sidebar.fireSimulation.waterConsumption') }}:
            </v-col>
            <v-col cols="7">
              <v-row>
                <v-col cols="12">
                  <v-radio-group
                    class="my-0 py-0 input--type"
                    v-model="waterConsumptionType"
                    :rules="waterConsumptionRules"
                    dense
                    row
                    hide-details
                    @change="waterConsumptionValue = ''"
                  >
                    <v-radio :label="$i18n.t('dialog.value')" value="value"></v-radio>
                    <v-radio :label="$i18n.t('dialog.attribute')" value="attribute"></v-radio>
                  </v-radio-group>
                </v-col>
              </v-row>
              <v-row no-gutters style="width: 100%">
                <v-col>
                  <data-input
                    v-bind="waterConsumptionConfig"
                    :height="20"
                    v-model="waterConsumptionValue"
                    :rules="waterConsumptionRules"
                  />
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </sidebar-row>
      </v-form>
    </v-col>
  </v-row>
</template>
<script>
import { call, get, sync } from 'vuex-pathify';

import CardTitleRow from '@/components/CardTitleRow';
import DataInput from '@/components/DataInput';
import SidebarRow from '@/components/SidebarRow';

export default {
  name: 'FireSimulation',
  components: { CardTitleRow, DataInput, SidebarRow },
  computed: {
    layers: get('layers/layers'),
    hydrantsRoleLayerSelectedFeatures: get('map/selectedFeatures@:hydrantsRoleLayerId'),
    hydrantsSelectedFeatures() {
      return this.hydrantsRoleLayerSelectedFeatures || [];
    },
    hydrantsAttributes() {
      return this.$_flattenStructure(this.hydrantsRoleLayer.form_schema.elements, 'elements');
    },
    hydrantsRoleLayer() {
      return this.$_objectToArray(this.layers).find(layer => layer.role === 'water_hydrants');
    },
    hydrantsRoleLayerId() {
      return this.hydrantsRoleLayer?.id;
    },
    waterConsumptionConfig() {
      return this.waterConsumptionType === 'attribute'
        ? {
            itemText: 'label',
            itemValue: 'attribute',
            items: this.hydrantsAttributes,
            label: this.$i18n.t('dialog.body.attribute'),
            dataType: { name: 'select' },
          }
        : {
            label: this.$i18n.t('dialog.body.value'),
            dataType: { name: 'float' },
          };
    },
    waterConsumptionRules() {
      return [v => !!v || v === false || v === 0 || this.$i18n.t('rules.required')];
    },

    isStickyButtonsLoading: sync('sidebar/isStickyButtonsLoading'),
    isStickyButtonsValid: sync('sidebar/isStickyButtonsValid'),
    isStickyButtonsVisible: sync('sidebar/isStickyButtonsVisible'),
    stickyButtons: sync('sidebar/stickyButtons'),
  },
  data: () => ({
    isValid: false,
    waterConsumptionType: 'value',
    waterConsumptionValue: '',

    modelAdditionalParameters: localStorage.getItem('additionalGraphParameters')
      ? JSON.parse(localStorage.getItem('additionalGraphParameters'))
      : [],
    modelNetKind: localStorage.getItem('netKindModel') ? JSON.parse(localStorage.getItem('netKindModel')) : [],
    modelStatus: localStorage.getItem('statusModel') ? JSON.parse(localStorage.getItem('statusModel')) : [],
  }),
  methods: {
    runModel: call('epanet/runModel'),
    getPayload() {
      const additionalParamsArray = [];
      this.modelAdditionalParameters.forEach(param => {
        if (param.checked) {
          additionalParamsArray.push({
            filter: param.filter,
            datasource: param.datasource,
          });
        }
      });
      const hydrantsDemand = {
        hydrants: this.hydrantsSelectedFeatures,
      };
      hydrantsDemand[this.waterConsumptionType] = this.waterConsumptionValue;
      const payload = {
        status: this.modelStatus,
        net_kind: this.modelNetKind,
        features_filter: additionalParamsArray,
        hydrants_demand: hydrantsDemand,
      };
      return payload;
    },
    async runSimulation() {
      this.isStickyButtonsLoading = true;
      try {
        const payload = this.getPayload();
        const r = await this.runModel(payload);
        this.$router.push({
          name: 'modelingResults',
          params: {
            netType: 'water',
            scenarioId: r.scenario_id,
          },
        });
        localStorage.setItem(`scenarioId-water`, r.scenario_id);
      } finally {
        this.isStickyButtonsLoading = false;
      }
    },
  },
  mounted() {
    this.$watch(
      vm => {
        return { formValid: vm.isValid, selectedFeatures: vm.hydrantsSelectedFeatures };
      },
      nV => {
        const { formValid, selectedFeatures } = nV;
        this.isStickyButtonsValid = formValid && selectedFeatures?.length ? true : false;
      }
    );
    this.$root.$off('runFireSimulation');
    this.$root.$on('runFireSimulation', this.runSimulation);
    this.stickyButtons = [
      {
        action: 'runFireSimulation',
        translationPath: 'dialog.agreeRun',
      },
    ];
    this.isStickyButtonsLoading = false;
    this.isStickyButtonsVisible = true;
  },
  beforeDestroy() {
    this.isStickyButtonsVisible = false;
    this.stickyButtons = [];
  },
};
</script>
<style lang="scss" scoped>
.fire-simulation-info {
  font-family: 'Roboto';
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 16px;
}
::v-deep {
  .input--type {
    .v-icon {
      font-size: 15px;
    }
    .v-label {
      font-size: 12px;
    }
    .v-input--selection-controls__ripple {
      width: 15px;
      height: 15px;
      left: -3px;
      top: calc(50% - 15px) !important;
    }
    .v-input--selection-controls__input {
      margin-right: 0;
    }
  }
}
</style>
